import React, { useState, useEffect } from 'react'
import styles from './EmailVerificationModal.module.scss'
import Button from 'components/Button/Button'

export default function EmailVerificationModal({ open, onClose, onVerified, userEmail }) {
    const [step, setStep] = useState('send') // send, verify, success
    const [code, setCode] = useState(['', '', '', '', '', ''])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [message, setMessage] = useState(null)
    const [expiresAt, setExpiresAt] = useState(null)
    const [timeLeft, setTimeLeft] = useState(null)

    useEffect(() => {
        if (!open) {
            // Сброс состояния при закрытии
            setStep('send')
            setCode(['', '', '', '', '', ''])
            setError(null)
            setMessage(null)
            setExpiresAt(null)
            setTimeLeft(null)
        }
    }, [open])

    // Таймер обратного отсчёта
    useEffect(() => {
        if (!expiresAt) return

        const updateTimer = () => {
            const now = new Date()
            const expires = new Date(expiresAt)
            const diff = expires - now

            if (diff <= 0) {
                setTimeLeft(null)
                setError('Срок действия кода истёк. Запросите новый код.')
                return
            }

            const minutes = Math.floor(diff / 60000)
            const seconds = Math.floor((diff % 60000) / 1000)
            setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`)
        }

        updateTimer()
        const interval = setInterval(updateTimer, 1000)

        return () => clearInterval(interval)
    }, [expiresAt])

    const handleSendCode = async () => {
        setLoading(true)
        setError(null)
        setMessage(null)

        try {
            const token = localStorage.getItem('access_token')
            console.log('📧 Sending verification code...');
            console.log('   - Email:', userEmail);
            
            const response = await fetch('https://security.ncore.kg/api/account/send-verification-code/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            })

            const data = await response.json()
            console.log('📥 Server response:', data);
            console.log('   - Response status:', response.status);

            if (!response.ok) {
                console.error('❌ Server returned error:', data);
                throw new Error(data.error || 'Ошибка отправки кода')
            }

            console.log('✅ Verification code sent:', data)
            setMessage(`Код отправлен на ${data.email}`)
            setExpiresAt(data.expires_at)
            setStep('verify')
        } catch (err) {
            console.error('❌ Error sending code:', err)
            setError(err.message || 'Не удалось отправить код')
        } finally {
            setLoading(false)
        }
    }

    const handleVerifyCode = async () => {
        const codeString = code.join('')
        
        if (codeString.length !== 6) {
            setError('Введите полный код из 6 цифр')
            return
        }

        setLoading(true)
        setError(null)

        try {
            const token = localStorage.getItem('access_token')
            const response = await fetch('https://security.ncore.kg/api/account/verify-email/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ code: codeString })
            })

            const data = await response.json()

            if (!response.ok) {
                throw new Error(data.error || 'Неверный код')
            }

            console.log('✅ Email verified:', data)
            setStep('success')
            
            // Обновляем профиль через 1 секунду
            setTimeout(() => {
                if (onVerified) {
                    onVerified(data.user)
                }
                onClose()
            }, 1500)
        } catch (err) {
            console.error('❌ Error verifying code:', err)
            setError(err.message || 'Неверный код верификации')
        } finally {
            setLoading(false)
        }
    }

    const handleCodeInput = (index, value) => {
        // Разрешаем только цифры
        if (value && !/^\d$/.test(value)) return

        const newCode = [...code]
        newCode[index] = value

        setCode(newCode)
        setError(null)

        // Автоматический переход к следующему полю
        if (value && index < 5) {
            const nextInput = document.getElementById(`code-${index + 1}`)
            if (nextInput) nextInput.focus()
        }
    }

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            const prevInput = document.getElementById(`code-${index - 1}`)
            if (prevInput) prevInput.focus()
        }
    }

    const handlePaste = (e) => {
        e.preventDefault()
        const pasteData = e.clipboardData.getData('text').trim()
        
        // Проверяем, что это 6 цифр
        if (!/^\d{6}$/.test(pasteData)) return

        const newCode = pasteData.split('')
        setCode(newCode)
        
        // Фокус на последнее поле
        const lastInput = document.getElementById('code-5')
        if (lastInput) lastInput.focus()
    }

    if (!open) return null

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <button className={styles.closeBtn} onClick={onClose}>×</button>

                {step === 'send' && (
                    <div className={styles.content}>
                        <div className={styles.icon}>✉️</div>
                        <h2>Верификация Email</h2>
                        <p className={styles.description}>
                            Подтвердите ваш email адрес для доступа ко всем функциям платформы
                        </p>
                        <div className={styles.emailDisplay}>
                            <span className={styles.emailIcon}>📧</span>
                            <span className={styles.email}>{userEmail}</span>
                        </div>
                        {message && <div className={styles.success}>{message}</div>}
                        {error && <div className={styles.error}>{error}</div>}
                        <Button 
                            className={styles.sendBtn}
                            onClick={handleSendCode} 
                            disabled={loading}
                        >
                            {loading ? 'Отправка...' : 'Отправить код'}
                        </Button>
                    </div>
                )}

                {step === 'verify' && (
                    <div className={styles.content}>
                        <div className={styles.icon}>🔐</div>
                        <h2>Введите код</h2>
                        <p className={styles.description}>
                            Мы отправили код из 6 цифр на<br />
                            <strong>{userEmail}</strong>
                        </p>
                        
                        <div className={styles.codeInputs} onPaste={handlePaste}>
                            {code.map((digit, index) => (
                                <input
                                    key={index}
                                    id={`code-${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleCodeInput(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    className={styles.codeInput}
                                    autoFocus={index === 0}
                                />
                            ))}
                        </div>

                        {timeLeft && (
                            <div className={styles.timer}>
                                ⏱️ Код действителен: <strong>{timeLeft}</strong>
                            </div>
                        )}

                        {error && <div className={styles.error}>{error}</div>}

                        <div className={styles.actions}>
                            <Button 
                                className={styles.verifyBtn}
                                onClick={handleVerifyCode} 
                                disabled={loading || code.join('').length !== 6}
                            >
                                {loading ? 'Проверка...' : 'Подтвердить'}
                            </Button>
                            <button 
                                className={styles.resendBtn}
                                onClick={handleSendCode}
                                disabled={loading}
                            >
                                Отправить код повторно
                            </button>
                        </div>
                    </div>
                )}

                {step === 'success' && (
                    <div className={styles.content}>
                        <div className={styles.successIcon}>✅</div>
                        <h2>Email подтверждён!</h2>
                        <p className={styles.description}>
                            Ваш email успешно верифицирован.<br />
                            Теперь у вас есть доступ ко всем функциям платформы.
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}
